/**
 * Fleet stream views, grouped by site — the pages behind the admin dashboard's
 * "Live Now" and "Upcoming Streams" tiles.
 *
 * One component, two modes, registered at two routes. They share the same data
 * (a single /fleet-overview call) and the same grouped layout; only the framing
 * differs, so splitting them into two files would just be two copies to keep in
 * step.
 *
 * "Today" is each site's own local day, not a UTC day — the fleet runs from
 * Vancouver to Dumfries, so a UTC day would put half the list on the wrong date.
 * The server does that arithmetic; this page renders times in each site's zone.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Radio, CalendarClock, Loader2, AlertCircle, AlertTriangle, ChevronLeft, WifiOff, Zap } from 'lucide-react';

import { schedulesApi, queryKeys } from '@/lib/api';

type Mode = 'live' | 'upcoming';

/** Wall-clock time at a site, e.g. "7:15 PM". */
function siteTime(iso: string | Date, timezone: string): string {
  try {
    return new Date(iso).toLocaleTimeString('en-US', {
      hour: 'numeric', minute: '2-digit', timeZone: timezone,
    });
  } catch {
    return new Date(iso).toISOString().slice(11, 16) + ' UTC';
  }
}

/** "in 12 min" / "in 3 h 5 min" — for the upcoming view only. */
function startsIn(iso: string | Date): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'now';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `in ${mins} min`;
  const h = Math.floor(mins / 60);
  return `in ${h} h ${mins % 60} min`;
}

/** "Thu Oct 15, 2:00 PM" in the site's zone — issues can be days ahead. */
function siteDateTime(iso: string, timezone: string): string {
  try {
    return new Date(iso).toLocaleString('en-US', { timeZone: timezone, weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return new Date(iso).toLocaleString(); }
}

function Matchup({ team1, team2, eventName }: { team1: string | null; team2: string | null; eventName: string | null }) {
  const t1 = (team1 ?? '').trim();
  const t2 = (team2 ?? '').trim();
  // Sources that leave the team columns blank get the league name copied into
  // both, so print it once rather than "X vs X".
  const same = !!t1 && t1.localeCompare(t2, undefined, { sensitivity: 'accent' }) === 0;
  const pair = same ? t1 : t1 && t2 ? `${t1} vs ${t2}` : t1 || t2;
  return (
    <span>
      <span className="text-gray-900 dark:text-gray-100">{pair || eventName || '—'}</span>
      {pair && eventName && pair !== eventName && (
        <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{eventName}</span>
      )}
    </span>
  );
}

export default function FleetStreamsPage({ mode }: { mode: Mode }) {
  const { data, isLoading, error, dataUpdatedAt } = useQuery({
    queryKey: queryKeys.schedules.fleetOverview(),
    queryFn: () => schedulesApi.getFleetOverview(),
    // On air now changes minute to minute; keep it fresh without hammering the
    // 1-core box — this endpoint reads heartbeats, which are write-heavy.
    refetchInterval: 30000,
  });

  const isLive = mode === 'live';
  const groups = (isLive ? data?.live : data?.upcoming) ?? [];
  const count = isLive ? data?.counts.live ?? 0 : data?.counts.upcomingToday ?? 0;
  const sites = isLive ? data?.counts.liveSites ?? 0 : data?.counts.upcomingSites ?? 0;

  return (
    <div className="space-y-5 p-6">
      <div>
        <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900 dark:hover:text-gray-100">
          <ChevronLeft className="h-4 w-4" /> Dashboard
        </Link>
        <div className="mt-2 flex items-center gap-3">
          <span className={`rounded-lg p-2.5 ${isLive
            ? 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400'
            : 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'}`}>
            {isLive ? <Radio className="h-5 w-5" /> : <CalendarClock className="h-5 w-5" />}
          </span>
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {isLive ? 'Live Now' : 'Upcoming Streams'}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {isLoading
                ? 'Loading…'
                : count === 0
                  ? (isLive ? 'Nothing on air right now.' : 'Nothing else scheduled today.')
                  : `${count} stream${count === 1 ? '' : 's'} across ${sites} site${sites === 1 ? '' : 's'}`}
              {!isLive && count > 0 && ' — remaining today, in each site’s local time'}
              {!isLive && (data?.counts.issueSites ?? 0) > 0 && (
                <span className="ml-1 font-medium text-amber-700 dark:text-amber-400">
                  · {data!.counts.issueSites} site{data!.counts.issueSites === 1 ? '' : 's'} with schedule issues
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
        <Link
          to={isLive ? '/streams/upcoming' : '/streams/live'}
          className="rounded border border-gray-200 px-2 py-1 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
          {isLive ? 'See upcoming today →' : 'See what’s live now →'}
        </Link>
        {dataUpdatedAt > 0 && <span>updated {new Date(dataUpdatedAt).toLocaleTimeString()}</span>}
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
          <AlertCircle className="h-4 w-4" />
          {error instanceof Error ? error.message : 'Could not load streams'}
        </div>
      )}

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Reading fleet state…
        </div>
      )}

      {!isLoading && !error && groups.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 px-6 py-10 text-center dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {isLive
              ? 'No site is streaming at the moment.'
              : 'No site has another stream scheduled before the end of its local day.'}
          </p>
        </div>
      )}

      <div className="space-y-4">
        {groups.map((g: any) => (
          <div key={g.facilityId} className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-2.5 dark:border-gray-800">
              <div className="flex items-center gap-2">
                <Link to={`/facilities/${g.facilityId}`} className="text-sm font-semibold text-gray-900 hover:underline dark:text-gray-100">
                  {g.facilityName}
                </Link>
                <span className="text-xs text-gray-400">{g.facilityId}</span>
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                  {isLive ? g.entries.length : g.rows.length}
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                {!g.agentOnline && (
                  <span className="inline-flex items-center gap-1 rounded bg-amber-50 px-2 py-0.5 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                    <WifiOff className="h-3 w-3" /> agent offline
                  </span>
                )}
                {/* The agent is the source of truth for what is on air. When it is
                    not answering we fall back to schedule rows, which can be stale
                    — say so rather than implying these are confirmed live. */}
                {isLive && g.fromDatabase && (
                  <span className="text-amber-600 dark:text-amber-400">from schedule, unconfirmed</span>
                )}
                <span className="text-gray-400">{g.timezone}</span>
              </div>
            </div>

            {/* Every schedule issue for the site, in full — not only today's. CSM's
                own sheet-preparation failures (kind 'prep') are in red: the
                schedule has stopped updating. The rest are the source's data. */}
            {!isLive && g.issues?.length > 0 && (
              <div className="space-y-1.5 border-b border-gray-100 px-4 py-2.5 dark:border-gray-800">
                {g.issues.filter((it: any) => it.kind === 'prep').map((it: any, i: number) => (
                  <div key={`p${i}`} className="flex gap-2 rounded-md bg-red-50 px-3 py-1.5 text-xs text-red-800 dark:bg-red-950/30 dark:text-red-300">
                    <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span><span className="font-semibold">Schedule not updating.</span> {it.reason}</span>
                  </div>
                ))}
                {g.issues.some((it: any) => it.kind !== 'prep') && (
                  <div className="rounded-md bg-amber-50 px-3 py-1.5 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
                    <div className="mb-1 flex items-center gap-1.5 font-semibold">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      {g.issues.filter((it: any) => it.kind !== 'prep').length} schedule issue{g.issues.filter((it: any) => it.kind !== 'prep').length === 1 ? '' : 's'}
                    </div>
                    <ul className="space-y-1">
                      {g.issues.filter((it: any) => it.kind !== 'prep').map((it: any, i: number) => (
                        <li key={i} className="leading-snug">
                          <span className="font-medium">
                            {[it.sheet ? `Sheet ${it.sheet}` : null, it.start ? siteDateTime(it.start, g.timezone) : null, it.row ? `row ${it.row}` : null]
                              .filter(Boolean).join(' · ') || 'Schedule'}
                          </span>
                          {it.event && <span className="text-amber-800 dark:text-amber-300"> · {it.event}</span>}
                          <span className="block text-amber-800 dark:text-amber-300">{it.reason}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <table className="w-full text-sm">
              <tbody>
                {!isLive && g.rows.length === 0 && (
                  <tr><td className="px-4 py-2 text-xs text-gray-400 dark:text-gray-500">No more streams today.</td></tr>
                )}
                {isLive
                  ? g.entries.map((e: any, i: number) => (
                      <tr key={`${e.sheetId}-${i}`} className="border-t border-gray-50 first:border-t-0 dark:border-gray-800">
                        <td className="w-24 whitespace-nowrap px-4 py-2 font-medium text-gray-700 dark:text-gray-300">Sheet {e.sheetId}</td>
                        <td className="px-2 py-2">
                          {e.schedule
                            ? <Matchup team1={e.schedule.team1Name} team2={e.schedule.team2Name} eventName={e.schedule.eventName} />
                            : e.adhoc
                              ? <Matchup team1={e.adhoc.team1Name} team2={e.adhoc.team2Name} eventName={e.adhoc.eventName} />
                              : <span className="text-gray-500 dark:text-gray-400">—</span>}
                        </td>
                        <td className="w-40 px-2 py-2 text-xs text-gray-500 dark:text-gray-400">
                          {e.schedule
                            ? `${siteTime(e.schedule.scheduledStart, g.timezone)} – ${siteTime(e.schedule.scheduledEnd, g.timezone)}`
                            : e.adhoc?.startedAt ? `since ${siteTime(e.adhoc.startedAt, g.timezone)}` : ''}
                        </td>
                        <td className="w-36 px-2 py-2 text-right text-xs">
                          {e.unscheduled && (
                            <span className="inline-flex items-center gap-1 rounded bg-violet-50 px-2 py-0.5 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
                              <Zap className="h-3 w-3" /> ad-hoc
                            </span>
                          )}
                          {e.schedule?.isEventMatch && (
                            <span className="ml-1 rounded bg-blue-50 px-2 py-0.5 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">event</span>
                          )}
                        </td>
                        <td className="w-24 px-4 py-2 text-right text-xs">
                          {e.broadcastId
                            ? <a href={`https://youtube.com/watch?v=${e.broadcastId}`} target="_blank" rel="noreferrer"
                                 className="text-blue-600 hover:underline dark:text-blue-400">watch</a>
                            : <span className="text-gray-300 dark:text-gray-600">—</span>}
                        </td>
                      </tr>
                    ))
                  : g.rows.map((r: any) => (
                      <tr key={r.id} className="border-t border-gray-50 first:border-t-0 dark:border-gray-800">
                        <td className="w-24 whitespace-nowrap px-4 py-2 font-medium text-gray-700 dark:text-gray-300">Sheet {r.sheetId}</td>
                        <td className="px-2 py-2">
                          <Matchup team1={r.team1Name} team2={r.team2Name} eventName={r.eventName} />
                          {r.syncWarning && (
                            <span className="mt-0.5 flex gap-1 text-xs text-amber-700 dark:text-amber-400">
                              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />{r.syncWarning}
                            </span>
                          )}
                        </td>
                        <td className="w-40 px-2 py-2 text-xs text-gray-500 dark:text-gray-400">
                          {siteTime(r.scheduledStart, g.timezone)} – {siteTime(r.scheduledEnd, g.timezone)}
                        </td>
                        <td className="w-28 px-2 py-2 text-right text-xs text-gray-500 dark:text-gray-400">
                          {startsIn(r.scheduledStart)}
                        </td>
                        <td className="w-24 px-4 py-2 text-right text-xs">
                          {r.isEventMatch && (
                            <span className="rounded bg-blue-50 px-2 py-0.5 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">event</span>
                          )}
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}
